import { tripRunwayBriefForIcaos } from "./airports";
import { resolveAirportRunway } from "./airportRunways";
import { formatMoney, type CurrencyCode, type MoneyCatalogs } from "./catalogs";
import { CLASH_KIND_LABEL, detectClashes } from "./dispatchBrain";
import { formatJetALine, lookupJetA, type JetAOverride } from "./jetA";
import { liveCompanyInbox } from "./companySync";
import { contentXmlInventoryForAssignee, contentXmlScheduleIcaos } from "./contentXmlSchedule";
import { crewById, crewRolesLabel } from "./crew";
import { recentTransactions } from "./ledger";
import { AIRFRAME_STATUS_LABEL, anyInspectionOverdue, hoursToInspection, openSquawksFor } from "./maintenance";
import { MEL_SEVERITY_LABEL, openMelFor } from "./mel";
import { normalizeE164 } from "./phone";
import { pickCurrentFlightTicket } from "./flightGrounding";
import { DEFAULT_VOICE_AGENT_VOICE, XAI_VOICE_AGENT_MODEL, resolveVoiceAgentModel } from "./voiceModel";
import { formatWeatherBriefText, type WeatherBrief } from "./weatherBrief";
import {
  displayFlightNumber,
  normalizeFlightHint,
  tripRouteLabel,
} from "./flightNumber";
import { formatDiversionLine } from "./diversion";
import { spokenTripLabelPhonetic } from "./phonetic";
import { RELEASE_PACK_STATUS_LABEL, releasePackOf } from "./releasePack";
import {
  TRIP_SUPPORT_SERVICE_LABEL,
  TRIP_SUPPORT_STATUS_LABEL,
  openTripSupportCount,
  tripSupportOf,
} from "./tripSupport";
import { findVoiceInboxRow, spokenInboxRef, type VoiceInboxHint } from "./voiceLookup";
import type { TripAssignment } from "./assignment";
import type {
  AircraftSpec,
  CharterRequest,
  CompanyAccount,
  CompanyProfile,
  CrewMember,
  DeskRole,
  DutyStandbyState,
  FlightLogEntry,
  MelItem,
  Mission,
  PilotLogbookEntry,
  ReleasePackStatus,
  SceneryAirport,
  Squawk,
  TripSupportLine,
  VoiceSettings,
} from "./types";
import type { DutyRoster, DutyView } from "./duty";
import { dutyViews } from "./duty";
import type { PilotProfile } from "./types";

export const DEFAULT_VOICE_SETTINGS: VoiceSettings = {
  enabled: true,
  voiceId: DEFAULT_VOICE_AGENT_VOICE,
  model: XAI_VOICE_AGENT_MODEL,
};

export const DEFAULT_DUTY_STANDBY: DutyStandbyState = {
  armed: false,
  windows: [],
  seenInboxIds: [],
};

export function hydrateVoiceSettings(raw?: Partial<VoiceSettings> | null): VoiceSettings {
  const voiceId =
    typeof raw?.voiceId === "string" && raw.voiceId.trim() ? raw.voiceId.trim() : DEFAULT_VOICE_AGENT_VOICE;
  // The old floating alias was never a deliberate pin — move those desks to the
  // versioned flagship. A real pin (any other id) is left alone.
  const model = resolveVoiceAgentModel(raw?.model);
  return {
    enabled: raw?.enabled !== false,
    voiceId,
    model,
  };
}

export function hydrateDutyStandby(raw?: Partial<DutyStandbyState> | null): DutyStandbyState {
  const windows = Array.isArray(raw?.windows)
    ? raw.windows.filter((w) => w && typeof w.startUtc === "string" && typeof w.endUtc === "string")
    : [];
  return {
    armed: Boolean(raw?.armed),
    armedAtUtc: typeof raw?.armedAtUtc === "string" ? raw.armedAtUtc : undefined,
    windows: windows.map((w) => ({
      id: w.id || `win-${w.startUtc}`,
      startUtc: w.startUtc,
      endUtc: w.endUtc,
      label: w.label ?? "",
    })),
    seenInboxIds: Array.isArray(raw?.seenInboxIds) ? raw.seenInboxIds.filter((id) => typeof id === "string") : [],
    lastCallInboxId: typeof raw?.lastCallInboxId === "string" ? raw.lastCallInboxId : undefined,
  };
}

export function isCharterInboxItem(row: Pick<CharterRequest, "kind">): boolean {
  return !row.kind || row.kind === "charter";
}

export type VoiceTrackerSnap = {
  enabled: boolean;
  client: "off" | "idle" | "live" | "error" | "unset";
  callsign?: string;
  originIcao?: string;
  destIcao?: string;
  phase?: string;
};

export type VoiceSyncSnap = {
  configured: boolean;
  status: "unset" | "checking" | "ok" | "error";
  label: string;
};

export type VoiceDeskCrewAccount = {
  id: string;
  username: string;
  displayName: string;
  roles: DeskRole[];
  roleLabel: string;
  phone: string;
};

export type VoiceDeskSignedOn = {
  id: string;
  username: string;
  displayName: string;
  roleLabel: string;
};

/** Resolved PSTN / desk caller. Unknown numbers stay generic ("Dispatch"). */
export type VoiceCaller = {
  known: boolean;
  displayName: string;
  username: string;
  role?: DeskRole;
  roleLabel: string;
  phone: string;
  crewId?: string;
};

export const UNKNOWN_VOICE_CALLER: VoiceCaller = {
  known: false,
  displayName: "Dispatch",
  username: "",
  roleLabel: "",
  phone: "",
};

export type VoiceCrewPhoneRow = {
  id?: string;
  username?: string;
  displayName?: string;
  roles?: DeskRole[];
  roleLabel?: string;
  phone?: string;
};

export type VoiceDeskInput = {
  role: DeskRole;
  company: CompanyProfile;
  profile: PilotProfile;
  crew: CrewMember[];
  currentCrewId?: string | null;
  /** Twilio inbound `From` (E.164). When set, snapshot.caller is resolved against crew phones. */
  from?: string;
  scenery: SceneryAirport[];
  fleet: AircraftSpec[];
  inbox: CharterRequest[];
  missions: Mission[];
  flightLogs: FlightLogEntry[];
  logbook: PilotLogbookEntry[];
  account: CompanyAccount;
  currency?: CurrencyCode;
  catalogs?: MoneyCatalogs;
  jetAOverrides?: JetAOverride[];
  squawks: Squawk[];
  melItems: MelItem[];
  duty: DutyRoster;
  standby: DutyStandbyState;
  voice: VoiceSettings;
  weather?: WeatherBrief | null;
  tracker?: VoiceTrackerSnap | null;
  sync?: VoiceSyncSnap | null;
  now?: Date;
  aircraftState?: { phase: string; label: string; currentIcao: string | null; note: string };
  jobMode?: string;
  assignments?: TripAssignment[];
  /** Flight plans selection. Megan uses it when the caller does not name a ticket. */
  planFocusId?: string | null;
};

export type VoiceDeskSnapshot = {
  role: DeskRole;
  /** Nanode company.id — Megan binds inbox/board to this, not last-writer voice desk. */
  companyId?: string;
  companyName: string;
  homeIcao: string;
  extraBases: string[];
  scheduleOnlyContentXmlAirports?: boolean;
  contentXmlIcaos?: string[];
  destinations: {
    icao: string;
    name: string;
    city: string;
    runwayFt?: number;
    jetA?: { pricePerUsGal: number; currency: string; source: string; line: string };
  }[];
  inbox: ReturnType<typeof summarizeInbox>;
  board: ReturnType<typeof summarizeBoard>;
  fleet: ReturnType<typeof summarizeFleet>;
  /** Full company book. Tools that plan fuel, range, or SimBrief read this, not the spoken summary. */
  liveFleet: AircraftSpec[];
  mel: ReturnType<typeof summarizeMel>;
  mx: ReturnType<typeof summarizeMx>;
  ledger: { balanceCad: number; balanceLabel: string; recent: { kind: string; amountCad: number; memo: string }[] };
  logs: ReturnType<typeof summarizeLogs>;
  logbook: ReturnType<typeof summarizeLogbook>;
  duty: ReturnType<typeof summarizeDuty>;
  /** Company crew/accounts with phones — source for inbound caller identity. */
  crew: VoiceDeskCrewAccount[];
  signedOn?: VoiceDeskSignedOn;
  /** Set only when `from` was provided at snapshot build. PSTN re-resolves From at session.update. */
  caller?: VoiceCaller;
  weatherText: string;
  weatherUnavailable: boolean;
  tracker: VoiceTrackerSnap;
  sync: VoiceSyncSnap;
  standby: { armed: boolean; windows: { label: string; startUtc: string; endUtc: string }[] };
  voiceEnabled: boolean;
  aircraftState?: { phase: string; label: string; currentIcao: string | null; note: string };
  jobMode?: string;
  assignments: ReturnType<typeof summarizeAssignments>;
  /** Mark Active / current ticket — never invented kinematics. */
  currentFlight: VoiceCurrentFlightSnap;
  /** detectClashes lean list. Empty is a valid "none on file". */
  clashes: VoiceClashSnap;
  /** Flight plans selection id, when the desk has one. */
  planFocusId?: string;
};

export type VoiceTripSupportLineSnap = {
  id: string;
  airportIcao: string;
  serviceType: string;
  serviceLabel: string;
  vendorName: string;
  status: string;
  statusLabel: string;
  estimatedCost?: number;
  currency?: string;
  notes: string;
  confirmByIso?: string;
};

export type VoiceReleasePackSnap = {
  status: ReleasePackStatus;
  statusLabel: string;
  updatedAtUtc?: string;
  releasedAtUtc?: string;
};

export type VoiceCurrentFlightSnap = {
  onFile: boolean;
  note: string;
  missionId?: string;
  flightNumber?: string;
  spokenLabel?: string;
  route?: string;
  originIcao?: string;
  destIcao?: string;
  status?: string;
  lifecycle?: string;
  registration?: string;
  divertToIcao?: string;
  divertReason?: string;
  departureIso?: string;
  arrivalIso?: string;
  phase?: string;
  trackerPhase?: string;
  trackerCallsign?: string;
};

export type VoiceClashSnap = {
  onFile: boolean;
  note: string;
  items: {
    id: string;
    kind: string;
    kindLabel: string;
    severity: string;
    message: string;
    flightNumbers: string[];
    missionIds: string[];
  }[];
};

function runwayFieldsForPair(originIcao?: string, destIcao?: string) {
  const brief = tripRunwayBriefForIcaos(originIcao, destIcao);
  return {
    originRunwayFt: resolveAirportRunway(originIcao).longestFt,
    destRunwayFt: resolveAirportRunway(destIcao).longestFt,
    runwayLine: brief.line,
    runwayNote: brief.warning,
  };
}

function summarizeInbox(inbox: CharterRequest[]) {
  return liveCompanyInbox(inbox).slice(0, 40).map((r) => ({
    id: r.id,
    kind: r.kind ?? "charter",
    source: r.source,
    status: r.status,
    client: r.clientName,
    clientCompany: r.clientCompany,
    route: `${r.originIcao}–${r.destIcao}`,
    originIcao: r.originIcao,
    destIcao: r.destIcao,
    ...runwayFieldsForPair(r.originIcao, r.destIcao),
    pax: r.paxCount,
    cargoLb: r.cargoLb,
    subject: r.emailSubject,
    suggestedAircraftId: r.suggestedAircraftId,
    departureIso: r.departureIso,
    spokenRef: spokenInboxRef({
      client: r.clientName,
      clientCompany: r.clientCompany,
      originIcao: r.originIcao,
      destIcao: r.destIcao,
    }),
  }));
}

function summarizeAssignments(assignments: TripAssignment[] | undefined) {
  return (assignments ?? [])
    .filter((a) => !a.superseded)
    .slice(0, 24)
    .map((a) => ({
      id: a.id,
      requestId: a.requestId,
      missionId: a.missionId,
      status: a.status,
      registration: a.registration,
      crewId: a.crewId,
      crewName: a.crewName,
      reasonCode: a.reasonCode,
    }));
}

export function isOpenBoardStatus(status?: string): boolean {
  const key = String(status ?? "").trim().toLowerCase().replace(/_/g, "-");
  return key === "pending" || key === "in-progress" || key === "scheduled" || key === "filed";
}

export const BOARD_SPOKEN_EMPTY =
  "Board is empty. No scheduled trips on file. Say that immediately — do not wait or call more tools.";

/** Verbatim TTS lines — used when the model would otherwise go silent on empty / fail. */
export const BOARD_SPOKEN_CANT_READ = "I can't read the live board right now.";
export const BOARD_SPOKEN_EMPTY_LINE = "The board is empty. No scheduled trips on file.";
export const BOARD_SPOKEN_NOTHING_SCHEDULED = "Nothing scheduled. No open trips on the board.";

export type SpeakableBoardTrip = {
  id: string;
  callsign: string;
  flightNumber: string;
  spokenLabel: string;
  status: string;
  registration: string;
  aircraftId?: string;
  route: string;
  originIcao: string;
  destIcao: string;
  lifecycle?: string;
  divertToIcao?: string;
  divertReason?: string;
  departureIso?: string;
  pax?: number;
  flightType?: string;
};

function asBoardRecord(row: unknown): Record<string, unknown> | null {
  return row && typeof row === "object" ? (row as Record<string, unknown>) : null;
}

export function leanBoardTrip(row: unknown): SpeakableBoardTrip | null {
  const m = asBoardRecord(row);
  if (!m) return null;
  const id = String(m.id ?? "").trim();
  if (!id) return null;
  const originIcao = String(m.originIcao ?? "").trim().toUpperCase();
  const destIcao = String(m.destIcao ?? "").trim().toUpperCase();
  const flightNumber = String(m.flightNumber || m.callsign || "").trim();
  const route = originIcao && destIcao ? `${originIcao}–${destIcao}` : String(m.route ?? "").trim();
  const divertToIcao = String(m.divertToIcao ?? "").trim().toUpperCase() || undefined;
  const divertReason = String(m.divertReason ?? "").trim() || undefined;
  const lifecycle = String(m.lifecycle ?? "").trim() || undefined;
  const divertLine = formatDiversionLine({
    lifecycle: lifecycle === "diverted" ? "diverted" : lifecycle === "diverting" ? "diverting" : undefined,
    divertToIcao,
    divertReason,
  });
  const baseSpoken =
    String(m.spokenLabel ?? "").trim() ||
    spokenTripLabelPhonetic({
      flightNumber,
      callsign: String(m.callsign ?? ""),
      registration: String(m.registration ?? ""),
      originIcao,
      destIcao,
    });
  const spoken = divertLine ? `${baseSpoken}, ${divertLine}` : baseSpoken;
  const paxRaw = m.pax ?? m.paxCount;
  const pax = typeof paxRaw === "number" && Number.isFinite(paxRaw) ? paxRaw : undefined;
  return {
    id,
    callsign: flightNumber || String(m.callsign ?? ""),
    flightNumber,
    spokenLabel: spoken,
    status: String(m.status ?? ""),
    registration: String(m.registration ?? ""),
    aircraftId: typeof m.aircraftId === "string" ? m.aircraftId : undefined,
    route,
    originIcao,
    destIcao,
    lifecycle,
    divertToIcao,
    divertReason,
    departureIso: typeof m.departureIso === "string" ? m.departureIso : undefined,
    pax,
    flightType: typeof m.flightType === "string" ? m.flightType : undefined,
  };
}

/** Open trips first, then closed — cap 16. Empty board is a valid speakable result. */
export function pickSpeakableBoardTrips(board: unknown): SpeakableBoardTrip[] {
  const rows = (Array.isArray(board) ? board : []).map(leanBoardTrip).filter((row): row is SpeakableBoardTrip => Boolean(row));
  const open = rows.filter((m) => isOpenBoardStatus(m.status));
  return (open.length ? open : rows).slice(0, 16);
}

export function speakableBoardNote(board: unknown): string {
  const rows = (Array.isArray(board) ? board : []).map(leanBoardTrip).filter((row): row is SpeakableBoardTrip => Boolean(row));
  if (!rows.length) return BOARD_SPOKEN_EMPTY;
  const open = rows.filter((m) => isOpenBoardStatus(m.status));
  const speak = (open.length ? open : rows).slice(0, 16);
  const labels = speak.map((t) => t.spokenLabel).filter(Boolean);
  if (!open.length) {
    return `No open scheduled trips. ${speak.length} closed trip${speak.length === 1 ? "" : "s"} on file: ${labels.join("; ")}. Say there is nothing scheduled.`;
  }
  return `Speak this board rundown now: ${labels.join("; ")}`;
}

/** Empty / fail-closed / nothing-scheduled must be spoken even if the model stalls. */
export function spokenBoardFallback(result: { ok?: boolean; name?: string; data?: unknown } | null | undefined): string {
  if (!result || result.name !== "get_board") return "";
  if (!result.ok) return BOARD_SPOKEN_CANT_READ;
  const trips = Array.isArray(result.data) ? result.data : [];
  if (!trips.length) return BOARD_SPOKEN_EMPTY_LINE;
  const open = trips.filter((row) => isOpenBoardStatus((row as { status?: string }).status));
  if (!open.length) return BOARD_SPOKEN_NOTHING_SCHEDULED;
  return "";
}

function summarizeBoard(missions: Mission[]) {
  const list = Array.isArray(missions) ? missions : [];
  const open = list.filter((m) => isOpenBoardStatus(m.status));
  const rest = list.filter((m) => !isOpenBoardStatus(m.status));
  return [...open, ...rest].slice(0, 16).map((m) => {
    const depMs = Date.parse(m.departureIso);
    const arrivalIso =
      Number.isFinite(depMs) && Number.isFinite(m.eteMin)
        ? new Date(depMs + Math.max(0, m.eteMin) * 60_000).toISOString()
        : "";
    const flightNumber = displayFlightNumber(m);
    const route = tripRouteLabel(m) || `${m.originIcao}–${m.destIcao}`;
    const divertLine = formatDiversionLine(m);
    return {
      id: m.id,
      callsign: flightNumber || m.callsign,
      flightNumber,
      spokenLabel: divertLine ? `${spokenTripLabelPhonetic(m)}, ${divertLine}` : spokenTripLabelPhonetic(m),
      status: m.status,
      registration: m.registration,
      aircraftId: m.aircraftId,
      route,
      originIcao: m.originIcao,
      destIcao: m.destIcao,
      ...runwayFieldsForPair(m.originIcao, m.destIcao),
      departureIso: m.departureIso,
      arrivalIso,
      eteMin: m.eteMin,
      pax: m.paxCount,
      cargoLb: m.cargoLb ?? 0,
      purpose: m.purpose,
      isMaintenance: m.isMaintenance,
      flightType: m.flightType,
      ...(m.lifecycle ? { lifecycle: m.lifecycle } : {}),
      ...(m.divertToIcao || m.divertReason
        ? { divertToIcao: m.divertToIcao, divertReason: m.divertReason }
        : {}),
      ...(m.cancelReason
        ? { cancelReason: m.cancelReason, cancelReasonNote: m.cancelReasonNote }
        : {}),
      tripSupport: summarizeTripSupportLines(m),
      tripSupportOpen: openTripSupportCount(m),
      releasePack: summarizeReleasePack(m),
      ...(m.ofpAttachment?.originIcao
        ? {
            ofp: {
              onFile: true as const,
              originIcao: m.ofpAttachment.originIcao,
              destIcao: m.ofpAttachment.destIcao,
              route: m.ofpAttachment.route ?? "",
              match: m.ofpAttachment.match,
              registration: m.ofpAttachment.registration,
              aircraftType: m.ofpAttachment.aircraftType,
              callsign: m.ofpAttachment.callsign,
            },
          }
        : {}),
    };
  });
}

function summarizeTripSupportLines(mission: Pick<Mission, "tripSupport">): VoiceTripSupportLineSnap[] {
  return tripSupportOf(mission).map((line) => leanTripSupportLine(line));
}

export function leanTripSupportLine(line: TripSupportLine): VoiceTripSupportLineSnap {
  return {
    id: line.id,
    airportIcao: line.airportIcao,
    serviceType: line.serviceType,
    serviceLabel: TRIP_SUPPORT_SERVICE_LABEL[line.serviceType],
    vendorName: line.vendorName,
    status: line.status,
    statusLabel: TRIP_SUPPORT_STATUS_LABEL[line.status],
    ...(line.estimatedCost != null ? { estimatedCost: line.estimatedCost } : {}),
    ...(line.currency ? { currency: line.currency } : {}),
    notes: line.notes,
    ...(line.confirmByIso ? { confirmByIso: line.confirmByIso } : {}),
  };
}

function summarizeReleasePack(mission: Pick<Mission, "releasePack">): VoiceReleasePackSnap {
  const pack = releasePackOf(mission);
  return {
    status: pack.status,
    statusLabel: RELEASE_PACK_STATUS_LABEL[pack.status],
    ...(pack.updatedAtUtc ? { updatedAtUtc: pack.updatedAtUtc } : {}),
    ...(pack.releasedAtUtc ? { releasedAtUtc: pack.releasedAtUtc } : {}),
  };
}

export function summarizeCurrentFlight(
  missions: Mission[],
  company: CompanyProfile,
  tracker?: VoiceTrackerSnap | null,
  aircraftState?: VoiceDeskInput["aircraftState"],
): VoiceCurrentFlightSnap {
  const list = Array.isArray(missions) ? missions : [];
  if (!list.length) {
    return { onFile: false, note: "No current flight on the desk. Do not invent one." };
  }
  const picked = pickCurrentFlightTicket({
    missions: list,
    activeFlightId: company.activeFlightId,
    now: new Date(),
  });
  if (!picked) {
    return { onFile: false, note: "No current flight on the desk. Do not invent one." };
  }
  const depMs = Date.parse(picked.departureIso);
  const arrivalIso =
    Number.isFinite(depMs) && Number.isFinite(picked.eteMin)
      ? new Date(depMs + Math.max(0, picked.eteMin) * 60_000).toISOString()
      : "";
  const divertLine = formatDiversionLine(picked);
  return {
    onFile: true,
    note: "Current flight is the Mark Active / OCC ticket. Altitude, GS, and live ETA stay — unless tracker already has them. Do not invent kinematics.",
    missionId: picked.id,
    flightNumber: displayFlightNumber(picked) || picked.callsign,
    spokenLabel: divertLine ? `${spokenTripLabelPhonetic(picked)}, ${divertLine}` : spokenTripLabelPhonetic(picked),
    route: tripRouteLabel(picked) || `${picked.originIcao}–${picked.destIcao}`,
    originIcao: picked.originIcao,
    destIcao: picked.destIcao,
    status: picked.status,
    lifecycle: picked.lifecycle,
    registration: picked.registration,
    divertToIcao: picked.divertToIcao,
    divertReason: picked.divertReason,
    departureIso: picked.departureIso,
    arrivalIso,
    phase: aircraftState?.label || aircraftState?.phase,
    trackerPhase: tracker?.phase,
    trackerCallsign: tracker?.callsign,
  };
}

export function summarizeClashes(input: VoiceDeskInput): VoiceClashSnap {
  const missions = Array.isArray(input.missions) ? input.missions : [];
  if (!missions.length) {
    return { onFile: true, note: "No board tickets on file — no conflicts to score.", items: [] };
  }
  try {
    const clashes = detectClashes({
      missions,
      assignments: input.assignments ?? [],
      fleet: input.fleet,
      crew: input.crew,
      duty: input.duty,
      scenery: input.scenery,
      inbox: input.inbox,
      squawks: input.squawks,
      melItems: input.melItems,
      now: input.now,
      homeIcao: input.company.homeIcao,
    });
    const items = clashes.slice(0, 16).map((clash) => {
      const flightNumbers = clash.missionIds
        .map((id) => {
          const hit = missions.find((m) => m.id === id);
          return hit ? displayFlightNumber(hit) || hit.callsign : "";
        })
        .filter(Boolean);
      return {
        id: clash.id,
        kind: clash.kind,
        kindLabel: CLASH_KIND_LABEL[clash.kind],
        severity: clash.severity,
        message: clash.message,
        flightNumbers,
        missionIds: clash.missionIds,
      };
    });
    return {
      onFile: true,
      note: items.length
        ? `${items.length} conflict${items.length === 1 ? "" : "s"} on the desk. Speak only these — do not invent more.`
        : "No tail / crew / duty conflicts on file.",
      items,
    };
  } catch {
    return { onFile: false, note: "Conflict list is not on the desk. Do not invent one.", items: [] };
  }
}

function summarizeFleet(fleet: AircraftSpec[], squawks: Squawk[], melItems: MelItem[]) {
  return fleet.map((a) => ({
    id: a.id,
    registration: a.registration,
    name: a.name,
    icaoType: a.icaoType,
    status: a.status,
    statusLabel: AIRFRAME_STATUS_LABEL[a.status],
    available: a.available,
    hours: a.airframeHours,
    hoursToInspection: hoursToInspection(a),
    inspectionOverdue: anyInspectionOverdue(a),
    openSquawks: openSquawksFor(a.id, squawks).length,
    openMel: openMelFor(a.id, melItems).length,
    lastAirportIcao: a.lastAirportIcao ?? "",
  }));
}

function summarizeMel(items: MelItem[], fleet: AircraftSpec[]) {
  return items
    .filter((m) => m.status === "open")
    .slice(0, 20)
    .map((m) => ({
      id: m.id,
      aircraftId: m.aircraftId,
      registration: m.registration || fleet.find((a) => a.id === m.aircraftId)?.registration || "",
      ata: m.ata,
      severity: m.severity,
      severityLabel: MEL_SEVERITY_LABEL[m.severity],
      description: m.description,
      category: m.category,
    }));
}

function summarizeMx(fleet: AircraftSpec[], squawks: Squawk[]) {
  const open = squawks.filter((s) => s.status === "open").slice(0, 16);
  return {
    squawks: open.map((s) => ({
      id: s.id,
      aircraftId: s.aircraftId,
      registration: s.registration,
      severity: s.severity,
      title: s.title,
    })),
    inspections: fleet
      .filter((a) => anyInspectionOverdue(a) || hoursToInspection(a) <= 20)
      .map((a) => ({
        aircraftId: a.id,
        registration: a.registration,
        hoursToInspection: hoursToInspection(a),
        overdue: anyInspectionOverdue(a),
      })),
  };
}

function summarizeLogs(logs: FlightLogEntry[]) {
  return logs.slice(0, 10).map((l) => ({
    id: l.id,
    tripId: l.tripId,
    callsign: l.callsign,
    route: l.route,
    status: l.status,
    registration: l.registration,
  }));
}

function summarizeLogbook(rows: PilotLogbookEntry[]) {
  return rows.slice(0, 8).map((r) => ({
    id: r.id,
    crewName: r.crewName,
    route: r.route,
    registration: r.registration,
    role: r.role,
    flightTimeMin: r.flightTimeMin,
    dateUtc: r.dateUtc,
  }));
}

function summarizeDuty(views: DutyView[]) {
  return views.slice(0, 6).map((v) => ({
    crewId: v.crewId,
    crewName: v.crewName,
    status: v.status,
    statusLabel: v.statusLabel,
    fdpElapsedMin: v.fdpElapsedMin,
    flightTimeTodayMin: v.flightTimeTodayMin,
  }));
}

function primaryDeskRole(roles: DeskRole[] | undefined): DeskRole | undefined {
  if (!roles?.length) return undefined;
  if (roles.includes("dispatcher")) return "dispatcher";
  return roles[0];
}

export function summarizeVoiceCrew(crew: CrewMember[]): VoiceDeskCrewAccount[] {
  return crew.map((c) => ({
    id: c.id,
    username: c.id,
    displayName: c.displayName,
    roles: c.roles,
    roleLabel: crewRolesLabel(c.roles),
    phone: normalizeE164(c.phone) || c.phone.trim(),
  }));
}

export function signedOnAsCaller(signedOn?: VoiceDeskSignedOn | null): VoiceCaller | undefined {
  if (!signedOn?.displayName?.trim()) return undefined;
  return {
    known: true,
    displayName: signedOn.displayName.trim(),
    username: signedOn.username || signedOn.id,
    roleLabel: signedOn.roleLabel,
    phone: "",
    crewId: signedOn.id,
  };
}

export function unknownVoiceCaller(from = ""): VoiceCaller {
  return { ...UNKNOWN_VOICE_CALLER, phone: normalizeE164(from) || from.trim() };
}

export function voiceCallerFromCrew(row: VoiceCrewPhoneRow): VoiceCaller {
  const roles = Array.isArray(row.roles) ? row.roles : [];
  const displayName = row.displayName?.trim() || row.username?.trim() || row.id?.trim() || "Dispatch";
  const username = row.username?.trim() || row.id?.trim() || "";
  return {
    known: true,
    displayName,
    username,
    role: primaryDeskRole(roles),
    roleLabel: row.roleLabel?.trim() || crewRolesLabel(roles),
    phone: normalizeE164(row.phone ?? "") || (row.phone ?? "").trim(),
    crewId: row.id,
  };
}

/** Match Twilio `From` (E.164) to a crew card phone. Unknown numbers stay generic. */
export function resolveVoiceCaller(from: string | undefined | null, crew: readonly VoiceCrewPhoneRow[] = []): VoiceCaller {
  const want = normalizeE164(from ?? "");
  if (!want) return unknownVoiceCaller(from ?? "");
  const hit = crew.find((c) => normalizeE164(c.phone ?? "") === want);
  if (!hit) return unknownVoiceCaller(want);
  return voiceCallerFromCrew(hit);
}

/** Read-only desk picture for voice tools. Never includes secrets. */
export function buildVoiceDeskSnapshot(input: VoiceDeskInput): VoiceDeskSnapshot {
  const now = input.now ?? new Date();
  const weather = input.weather ?? null;
  const weatherUnavailable = !weather || weather.wxSource === "none";
  const recent = recentTransactions(input.account, 6);
  const crew = summarizeVoiceCrew(input.crew);
  const signedMember = crewById(input.crew, input.currentCrewId) ?? input.crew[0];
  const signedOn: VoiceDeskSignedOn | undefined = signedMember
    ? {
        id: signedMember.id,
        username: signedMember.id,
        displayName: signedMember.displayName,
        roleLabel: crewRolesLabel(signedMember.roles),
      }
    : undefined;
  const caller = input.from !== undefined ? resolveVoiceCaller(input.from, crew) : undefined;
  const contentXmlIcaos = contentXmlScheduleIcaos(contentXmlInventoryForAssignee(input.crew, input.currentCrewId));
  const scheduleOnly = input.company.scheduleOnlyContentXmlAirports === true;
  const destRows = scheduleOnly
    ? input.scenery.filter((s) => contentXmlIcaos.includes(s.icao.toUpperCase()))
    : input.scenery;
  return {
    role: input.role,
    companyId: input.company.id,
    companyName: input.company.operatorName,
    homeIcao: input.company.homeIcao,
    extraBases: input.company.extraBases ?? [],
    scheduleOnlyContentXmlAirports: scheduleOnly,
    contentXmlIcaos,
    destinations: destRows.slice(0, 40).map((s) => {
      const jetA = lookupJetA({
        icao: s.icao,
        catalogs: input.catalogs,
        overrides: input.jetAOverrides,
        homeCurrency: input.currency ?? input.catalogs?.currency,
      });
      return {
        icao: s.icao,
        name: s.name,
        city: s.city,
        runwayFt: resolveAirportRunway(s.icao, { runwayFt: s.runwayFt, notes: s.notes }).longestFt,
        jetA: {
          pricePerUsGal: jetA.pricePerUsGal,
          currency: jetA.currency,
          source: jetA.source,
          line: formatJetALine(jetA),
        },
      };
    }),
    inbox: summarizeInbox(input.inbox),
    board: summarizeBoard(input.missions),
    fleet: summarizeFleet(input.fleet, input.squawks, input.melItems),
    liveFleet: input.fleet,
    mel: summarizeMel(input.melItems, input.fleet),
    mx: summarizeMx(input.fleet, input.squawks),
    ledger: {
      balanceCad: input.account.balanceCad,
      balanceLabel: formatMoney(input.account.balanceCad, input.currency ?? "CAD"),
      recent: recent.map((t) => ({ kind: t.kind, amountCad: t.amountCad, memo: t.memo })),
    },
    logs: summarizeLogs(input.flightLogs),
    logbook: summarizeLogbook(input.logbook),
    duty: summarizeDuty(dutyViews(input.duty, input.profile, now)),
    crew,
    signedOn,
    caller,
    weatherText: weather ? formatWeatherBriefText(weather) : "Wx unavailable — use the Weather desk. Nothing invented.",
    weatherUnavailable,
    tracker: input.tracker ?? { enabled: false, client: "unset" },
    sync: input.sync ?? { configured: false, status: "unset", label: "SYNC —" },
    standby: {
      armed: input.standby.armed,
      windows: input.standby.windows.map((w) => ({
        label: w.label,
        startUtc: w.startUtc,
        endUtc: w.endUtc,
      })),
    },
    voiceEnabled: input.voice.enabled,
    aircraftState: input.aircraftState,
    jobMode: input.jobMode,
    assignments: summarizeAssignments(input.assignments),
    currentFlight: summarizeCurrentFlight(input.missions, input.company, input.tracker, input.aircraftState),
    clashes: summarizeClashes(input),
    planFocusId: input.planFocusId?.trim() || undefined,
  };
}

export function focusRouteFromSnapshot(snap: VoiceDeskSnapshot): { originIcao: string; destIcao: string } {
  const open = snap.board.find((m) => m.status === "pending" || m.status === "in-progress");
  if (open) return { originIcao: open.originIcao, destIcao: open.destIcao };
  const charter = snap.inbox.find((r) => r.kind === "charter" && r.status === "new");
  if (charter) return { originIcao: charter.originIcao, destIcao: charter.destIcao };
  return { originIcao: snap.homeIcao, destIcao: snap.homeIcao };
}

export function findCharterOnDesk(
  snap: VoiceDeskSnapshot,
  hint?: VoiceInboxHint,
) {
  return findVoiceInboxRow(snap.inbox, hint ?? {}) ?? snap.inbox.find((r) => r.kind === "charter" && r.status === "new") ?? snap.inbox[0] ?? null;
}

export type BoardTripHint = {
  missionId?: string;
  flightNumber?: string;
  callsign?: string;
  registration?: string;
  originIcao?: string;
  destIcao?: string;
  route?: string;
};

export type BoardTripRow = VoiceDeskSnapshot["board"][number];

export function parseRouteHint(hint: BoardTripHint = {}): { originIcao?: string; destIcao?: string } {
  const o = String(hint.originIcao ?? "").trim().toUpperCase();
  const d = String(hint.destIcao ?? "").trim().toUpperCase();
  if (o && d) return { originIcao: o, destIcao: d };
  const raw = String(hint.route ?? "")
    .trim()
    .toUpperCase()
    .replace(/[–—]/g, "-")
    .replace(/\s+TO\s+/g, "-")
    .replace(/\s+/g, "");
  const match = raw.match(/^([A-Z0-9]{3,4})-([A-Z0-9]{3,4})$/);
  if (match) return { originIcao: match[1], destIcao: match[2] };
  return { originIcao: o || undefined, destIcao: d || undefined };
}

function tripIdentityHint(hint: BoardTripHint = {}): boolean {
  return Boolean(
    String(hint.missionId ?? "").trim() ||
      String(hint.flightNumber ?? "").trim() ||
      String(hint.callsign ?? "").trim() ||
      String(hint.registration ?? "").trim(),
  );
}

function tripMatchesIdentity(trip: BoardTripRow, hint: BoardTripHint): boolean {
  const missionId = String(hint.missionId ?? "").trim();
  if (missionId) return trip.id === missionId;
  const want = normalizeFlightHint(hint.flightNumber || hint.callsign || hint.registration || "");
  if (!want) return true;
  const numbers = [trip.flightNumber, trip.callsign].map(normalizeFlightHint).filter(Boolean);
  if (numbers.includes(want)) return true;
  const tail = normalizeFlightHint(trip.registration);
  if (tail && tail === want) return true;
  return false;
}

function tripMatchesRoute(trip: BoardTripRow, hint: BoardTripHint): boolean {
  const route = parseRouteHint(hint);
  if (route.originIcao && trip.originIcao.toUpperCase() !== route.originIcao) return false;
  if (route.destIcao && trip.destIcao.toUpperCase() !== route.destIcao) return false;
  return true;
}

export function formatCandidateList(trips: readonly BoardTripRow[]): string {
  if (!trips.length) return "No matching trips on the board.";
  return trips
    .slice(0, 8)
    .map((t) => t.spokenLabel || spokenTripLabelPhonetic(t))
    .join("; ");
}

export type BoardTripResolve =
  | { ok: true; trip: BoardTripRow }
  | { ok: false; error: string; candidates: BoardTripRow[] };

/**
 * Identify a board trip. Cancel / reinstate must pass flight number or
 * callsign (plus route when several share a tail). No silent first-open fallback.
 */
export function resolveBoardTrip(
  snap: VoiceDeskSnapshot,
  hint: BoardTripHint = {},
  opts?: { requireIdentity?: boolean },
): BoardTripResolve {
  const board = Array.isArray(snap.board) ? snap.board : [];
  const open = board.filter((m) => m.status === "pending" || m.status === "in-progress");
  const requireIdentity = opts?.requireIdentity === true;
  if (requireIdentity && !tripIdentityHint(hint) && !parseRouteHint(hint).originIcao) {
    return {
      ok: false,
      error: `Which trip? On the board: ${formatCandidateList(open.length ? open : board)}`,
      candidates: open.length ? open : board,
    };
  }
  let pool = board.filter((m) => tripMatchesIdentity(m, hint) && tripMatchesRoute(m, hint));
  if (String(hint.missionId ?? "").trim() && !pool.length) {
    return { ok: false, error: "That trip id is not on the board.", candidates: open };
  }
  if (pool.length === 1) return { ok: true, trip: pool[0]! };
  if (pool.length > 1) {
    return {
      ok: false,
      error: `Several trips match. Say the flight number and route: ${formatCandidateList(pool)}`,
      candidates: pool,
    };
  }
  if (requireIdentity) {
    return {
      ok: false,
      error: `No board trip matches that flight number / route. On file: ${formatCandidateList(open.length ? open : board)}`,
      candidates: open.length ? open : board,
    };
  }
  const fallback = open[0] ?? board[0];
  if (!fallback) return { ok: false, error: "No board trip on file.", candidates: [] };
  return { ok: true, trip: fallback };
}

/**
 * Flight plans ticket for Megan. A named flight wins. Otherwise the Flight
 * plans selection, then the current flight, then the only open trip.
 */
export function resolveSimbriefTicket(snap: VoiceDeskSnapshot, hint: BoardTripHint = {}): BoardTripResolve {
  const hinted = Boolean(
    String(hint.missionId ?? "").trim() ||
      String(hint.flightNumber ?? "").trim() ||
      String(hint.callsign ?? "").trim() ||
      String(hint.registration ?? "").trim() ||
      String(hint.route ?? "").trim() ||
      String(hint.originIcao ?? "").trim(),
  );
  if (hinted) return resolveBoardTrip(snap, hint, { requireIdentity: true });
  if (snap.planFocusId) {
    const focused = snap.board.find((row) => row.id === snap.planFocusId);
    if (focused) return { ok: true, trip: focused };
  }
  if (snap.currentFlight?.onFile && snap.currentFlight.missionId) {
    const current = snap.board.find((row) => row.id === snap.currentFlight?.missionId);
    if (current) return { ok: true, trip: current };
  }
  const open = snap.board.filter((row) => row.status === "pending" || row.status === "in-progress");
  if (open.length === 1) return { ok: true, trip: open[0]! };
  return resolveBoardTrip(snap, {}, { requireIdentity: true });
}

export function findBoardTrip(snap: VoiceDeskSnapshot, hint?: BoardTripHint) {
  const resolved = resolveBoardTrip(snap, hint ?? {}, { requireIdentity: tripIdentityHint(hint ?? {}) });
  return resolved.ok ? resolved.trip : null;
}

export function findTail(snap: VoiceDeskSnapshot, hint?: { aircraftId?: string; registration?: string }) {
  if (hint?.aircraftId) {
    const hit = snap.fleet.find((a) => a.id === hint.aircraftId);
    if (hit) return hit;
  }
  if (hint?.registration) {
    const reg = hint.registration.toUpperCase().replace(/\s+/g, "");
    const hit = snap.fleet.find((a) => a.registration.toUpperCase().replace(/\s+/g, "") === reg);
    if (hit) return hit;
  }
  return null;
}
